import { useEffect, useId, useRef, useState } from 'react';
import { IconClose, IconInfo } from '../icons.jsx';
import { normalizeConfirmationText } from '../ConfirmationDialog.jsx';
import './CampaignOverlays.css';

let openOverlays = 0;
let previousOverflow = '';

function Overlay({ variant, title, description, descriptionId, labelledBy, icon, children, footer,
  onClose, busy = false, role = 'dialog', returnFocus, requestId, confirmationOpen = false, className = '' }) {
  const id = useId();
  const dialog = useRef(null);
  const current = useRef({ onClose, busy });
  current.current = { onClose, busy };
  useEffect(() => {
    const node = dialog.current;
    const invoker = returnFocus || document.activeElement;
    if (openOverlays++ === 0) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    node.showModal();
    (node.querySelector('[data-campaign-safe-focus]:not(:disabled)') || node).focus({ preventScroll: true });
    return () => {
      node.close();
      if (--openOverlays === 0) document.body.style.overflow = previousOverflow;
      if (invoker?.isConnected) invoker.focus({ preventScroll: true });
    };
  }, [requestId, returnFocus]);
  useEffect(() => {
    if (busy && dialog.current?.contains(document.activeElement) && document.activeElement?.disabled) dialog.current.focus({ preventScroll: true });
  }, [busy]);
  // A generated `${id}-title` is only a real label target when this Overlay
  // itself renders that heading (the settings-drawer header, or a modal with
  // a title). Otherwise omit aria-labelledby rather than point it at nothing;
  // a caller that renders its own heading passes labelledBy explicitly.
  const ownTitleId = variant === 'settings-drawer' || (variant === 'modal' && title)
    ? `${id}-title`
    : undefined;
  return <dialog ref={dialog} className={`bl-campaign-overlay bl-campaign-${variant} ${className}`}
    role={role} aria-modal="true" aria-labelledby={labelledBy || ownTitleId}
    aria-describedby={description ? `${id}-description` : descriptionId}
    aria-busy={busy} tabIndex={-1}
    onKeyDown={(event) => {
      if (event.key !== 'Tab' || confirmationOpen) return;
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,[tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.getClientRects().length > 0 && !node.closest('[inert]'));
      if (!controls.length) { event.preventDefault(); dialog.current.focus(); return; }
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
        event.preventDefault(); first.focus();
      }
    }}
    onCancel={(event) => { event.preventDefault(); if (!current.current.busy && !confirmationOpen) current.current.onClose?.(); }}>
    {variant === 'settings-drawer' ? <header className="bl-campaign-overlay-header">
      <div><div className="bl-kicker">Quick campaign controls</div>
        <h2 id={`${id}-title`}>{title}</h2>
        {description && <p id={`${id}-description`}>{description}</p>}
      </div>
      <button type="button" className="bl-campaign-icon-button" data-campaign-safe-focus
        aria-label="Close ad settings" disabled={busy} onClick={onClose}>
        <IconClose size={16} aria-hidden="true" focusable="false" />
      </button>
    </header> : null}
    <div className="bl-campaign-overlay-body">
      {variant === 'modal' && <>
        {title && <>
          <div className="bl-campaign-modal-icon" aria-hidden="true">{icon || <IconInfo size={20} focusable="false" />}</div>
          <h2 id={`${id}-title`}>{title}</h2>
        </>}
        {description && <p id={`${id}-description`}>{description}</p>}
      </>}
      {children}
    </div>
    <footer className="bl-campaign-overlay-footer">{footer}</footer>
  </dialog>;
}

export function CampaignModal(props) { return <Overlay {...props} variant="modal" />; }
export function CampaignSettingsDrawer(props) { return <Overlay {...props} variant="settings-drawer" />; }

// Retain the shared request coordinator and stale-request safeguards while
// presenting campaign confirmations with the reference's compact modal.
// OGC-452: a destructive confirmation may carry a consequences ledger
// (`details`) and a typed confirmation (`confirmation`) that gates Confirm —
// the same options the shared ConfirmationDialog accepts.
function TypedConfirmation({ confirmation, value, matches, onChange, onSubmit }) {
  const id = useId();
  return <div className="bl-confirmation-typed">
    <label htmlFor={`${id}-typed`}>{confirmation.label}</label>
    <input id={`${id}-typed`} type="text" className="bl-themed-input" autoComplete="off"
      autoCapitalize="off" spellCheck={false} value={value} data-campaign-safe-focus
      aria-describedby={`${id}-hint`} aria-invalid={value && !matches ? 'true' : undefined}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (matches) onSubmit?.(event);
      }} />
    <p id={`${id}-hint`} className="bl-confirmation-typed-hint">
      {matches ? `Matches “${confirmation.expected}”.` : confirmation.hint}
    </p>
  </div>;
}

export function CampaignConfirmationDialog({ open, title, description, confirmLabel,
  cancelLabel = 'Cancel', onConfirm, onCancel, returnFocus, requestId, icon, children,
  details = [], confirmation = null, tone = 'accent' }) {
  // The typed value lives here so Confirm is gated synchronously from the
  // same render as the field — no effect-driven frame where a stale match
  // could enable the button. It resets whenever the request changes.
  const [typed, setTyped] = useState('');
  const [typedRequestId, setTypedRequestId] = useState(requestId);
  if (typedRequestId !== requestId) {
    setTypedRequestId(requestId);
    setTyped('');
  }
  const gated = Boolean(confirmation?.expected);
  const typedMatches = gated
    && normalizeConfirmationText(typedRequestId === requestId ? typed : '') === normalizeConfirmationText(confirmation.expected);
  const confirmEnabled = !gated || typedMatches;
  if (!open) return null;
  return <CampaignModal title={title} description={description} icon={icon}
    role="alertdialog" onClose={onCancel} returnFocus={returnFocus} requestId={requestId}
    className={tone === 'danger' ? 'is-danger' : ''}
    footer={<>
      <button type="button" data-campaign-safe-focus onClick={onCancel}>{cancelLabel}</button>
      <button type="button" className={`is-primary${tone === 'danger' ? ' is-danger' : ''}`}
        disabled={!confirmEnabled} aria-disabled={confirmEnabled ? undefined : 'true'}
        onClick={confirmEnabled ? () => onConfirm?.(gated ? { typedValue: typed } : undefined) : undefined}>{confirmLabel}</button>
    </>}>
    {Array.isArray(details) && details.length > 0 && <dl className="bl-confirmation-details" data-testid="confirmation-details">
      {details.map((entry) => <div key={entry.label} className="bl-confirmation-detail">
        <dt>{entry.label}</dt><dd>{entry.value}</dd>
      </div>)}
    </dl>}
    {gated && <TypedConfirmation confirmation={confirmation} value={typedRequestId === requestId ? typed : ''}
      matches={typedMatches} onChange={setTyped} onSubmit={() => onConfirm?.({ typedValue: typed })} />}
    {children}
  </CampaignModal>;
}
